import { useState } from 'react';
import { Link } from 'react-router-dom';

export interface FinalCTAProps {
  headline: string;
  subheadline: string;
  bgImage: string;
  formAction: string;
  phone?: string;
  checkItems?: string[];
}

export default function FinalCTASection({
  headline,
  subheadline,
  bgImage,
  formAction,
  phone = '(718) 555-0100',
  checkItems = ['Free estimates', 'In-home measurement', 'Professional installation'],
}: FinalCTAProps) {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', service: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.phone) return;
    setLoading(true);
    try {
      const body = new URLSearchParams({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        service: formData.service,
        message: formData.message,
      }).toString();
      await fetch(formAction, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body,
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="quote" className="relative py-28 px-6 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <img
          src={bgImage}
          alt="Premium flooring showroom"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-charcoal/55 via-charcoal/65 to-charcoal/75"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left: Text */}
          <div>
            <p className="text-bronze text-xs font-semibold uppercase tracking-widest mb-4">Ready to Begin?</p>
            <h2 className="font-serif text-5xl md:text-6xl font-semibold text-white leading-tight mb-6">
              {headline}
            </h2>
            <p className="text-white/80 text-lg mb-10 leading-relaxed">{subheadline}</p>

            <div className="flex flex-wrap gap-4 mb-10">
              <Link
                to="/#quote"
                className="bg-bronze text-white font-semibold px-8 py-4 rounded-md hover:bg-bronze-dark transition-colors whitespace-nowrap cursor-pointer flex items-center gap-2"
              >
                Get Free Estimate
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line"></i>
                </div>
              </Link>
              <a
                href={`tel:+1${phone.replace(/\D/g, '')}`}
                className="bg-white/10 backdrop-blur-sm border border-white/30 text-white font-semibold px-8 py-4 rounded-md hover:bg-white/20 transition-colors whitespace-nowrap cursor-pointer flex items-center gap-2"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-phone-line"></i>
                </div>
                Call Now: {phone}
              </a>
            </div>

            <div className="flex flex-wrap gap-6">
              {checkItems.map((item) => (
                <div key={item} className="flex items-center gap-2 text-white/75 text-sm">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-check-line text-bronze"></i>
                  </div>
                  {item}
                </div>
              ))}
            </div>
          </div>

          {/* Right: Form card */}
          <div className="bg-white rounded-2xl p-8">
            {submitted ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 flex items-center justify-center bg-sage/20 rounded-full mx-auto mb-4">
                  <i className="ri-check-line text-3xl text-sage"></i>
                </div>
                <h3 className="font-serif text-2xl font-semibold text-charcoal mb-2">Thank You!</h3>
                <p className="text-gray-500">We&apos;ve received your request and will be in touch within 24 hours to schedule your free in-home estimate.</p>
              </div>
            ) : (
              <form
                data-readdy-form
                onSubmit={handleSubmit}
                className="space-y-4"
              >
                <div>
                  <h3 className="font-serif text-2xl font-semibold text-charcoal mb-1">Get Your Free Estimate</h3>
                  <p className="text-gray-400 text-sm mb-6">No obligation — we respond within 24 hours</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-charcoal mb-1.5">Your Name *</label>
                    <input
                      type="text"
                      name="name"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Jane Smith"
                      className="w-full border border-warmgray rounded-md px-3 py-2.5 text-sm text-charcoal focus:outline-none focus:border-bronze transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-charcoal mb-1.5">Phone *</label>
                    <input
                      type="tel"
                      name="phone"
                      required
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="(718) 000-0000"
                      className="w-full border border-warmgray rounded-md px-3 py-2.5 text-sm text-charcoal focus:outline-none focus:border-bronze transition-colors"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-charcoal mb-1.5">Email *</label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="you@example.com"
                    className="w-full border border-warmgray rounded-md px-3 py-2.5 text-sm text-charcoal focus:outline-none focus:border-bronze transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-charcoal mb-1.5">Flooring Service Needed</label>
                  <select
                    name="service"
                    value={formData.service}
                    onChange={(e) => setFormData({ ...formData, service: e.target.value })}
                    className="w-full border border-warmgray rounded-md px-3 py-2.5 text-sm text-charcoal focus:outline-none focus:border-bronze transition-colors bg-white cursor-pointer"
                  >
                    <option value="">Select a service...</option>
                    <option>Carpet Installation</option>
                    <option>Tile &amp; Stone Flooring</option>
                    <option>Luxury Vinyl Plank (LVP)</option>
                    <option>Area Rug Selection</option>
                    <option>Showroom Consultation</option>
                    <option>Flooring Removal &amp; Replacement</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-charcoal mb-1.5">Project Details</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={(e) => {
                      if (e.target.value.length <= 500) {
                        setFormData({ ...formData, message: e.target.value });
                      }
                    }}
                    placeholder="Describe your space (room size, current flooring, desired material)..."
                    rows={3}
                    maxLength={500}
                    className="w-full border border-warmgray rounded-md px-3 py-2.5 text-sm text-charcoal focus:outline-none focus:border-bronze transition-colors resize-none"
                  />
                  <p className="text-xs text-gray-400 text-right mt-1">{formData.message.length}/500</p>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-bronze text-white font-semibold py-3.5 rounded-md hover:bg-bronze-dark transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
                >
                  {loading ? 'Sending...' : 'Request Free Estimate'}
                </button>

                <p className="text-xs text-gray-400 text-center">
                  Or call us directly:{' '}
                  <a href={`tel:+1${phone.replace(/\D/g, '')}`} className="text-bronze font-semibold cursor-pointer hover:underline">
                    {phone}
                  </a>
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
